/* eslint-disable react/prop-types */
import { AlertTriangle, ArrowDown, ArrowUp, Plus, RotateCcw, Save, Send, Trash2 } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useAdminAuth } from '../auth/AdminAuthProvider'
import { hasMinRole } from '../auth/adminAuthorization'
import {
  buildLocalShuttleCmsData, discardShuttleDraft, fetchShuttleContent, fetchShuttleVersions,
  formatDateTime, publishShuttleData, rollbackShuttleData, saveShuttleDraft, validateShuttleData,
} from '../lib/shuttleCms'

const SECTIONS = ['stops', 'routes', 'schedules']
const SECTION_LABELS = { stops: '정류장', routes: '노선', schedules: '시간표' }
const DAY_OPTIONS = [['mon', '월'], ['tue', '화'], ['wed', '수'], ['thu', '목'], ['fri', '금'], ['sat', '토'], ['sun', '일']]
const EMPTY_STOP = { id: '', name: '', lat: '', lng: '', type: 'external', memo: '', active: true }
const EMPTY_ROUTE = { id: '', name: '', stopIds: [], direction: 'toCampus', fare: '', memo: '', active: true }
const EMPTY_SCHEDULE = { routeId: '', departureTime: '08:30', days: ['mon', 'tue', 'wed', 'thu', 'fri'], semesterType: 'regular', exceptionDates: [], active: true }

function diffSummary(production, current) {
  const prod = production || { stops: [], routes: [], schedules: [] }
  const counts = section => ({ before: (prod[section] ?? []).length, after: (current[section] ?? []).length })
  return SECTIONS.map(section => ({ section, ...counts(section) }))
}

export default function ShuttlePage() {
  const auth = useAdminAuth()
  const canEdit = hasMinRole(auth.role, 'editor')
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [production, setProduction] = useState(null)
  const [draft, setDraft] = useState(null)
  const [data, setData] = useState(() => buildLocalShuttleCmsData())
  const [versions, setVersions] = useState([])
  const [section, setSection] = useState('stops')
  const [mode, setMode] = useState('table')
  const [jsonText, setJsonText] = useState('')
  const [selectedIndex, setSelectedIndex] = useState(null)
  const savedSnapshotRef = useRef('')

  async function load() {
    setLoading(true); setError('')
    try {
      const { draft: dr, production: prod } = await fetchShuttleContent()
      setDraft(dr); setProduction(prod)
      const initial = dr ?? prod ?? buildLocalShuttleCmsData({ status: 'draft' })
      setData(initial)
      savedSnapshotRef.current = JSON.stringify(initial)
      setVersions(await fetchShuttleVersions())
    } catch (err) { setError(err.message) } finally { setLoading(false) }
  }
  useEffect(() => { load() }, [])
  useEffect(() => { setJsonText(JSON.stringify(data, null, 2)) }, [data])

  const dirty = JSON.stringify(data) !== savedSnapshotRef.current
  const validation = useMemo(() => validateShuttleData(data), [data])
  const diff = useMemo(() => diffSummary(production, data), [production, data])
  const items = data[section] ?? []
  const selected = selectedIndex != null ? items[selectedIndex] : null

  function setFieldsForIndex(patch) {
    setData(current => {
      const list = [...(current[section] ?? [])]
      list[selectedIndex] = { ...list[selectedIndex], ...patch }
      return { ...current, [section]: list }
    })
  }

  function addItem() {
    const template = section === 'stops' ? EMPTY_STOP : section === 'routes' ? EMPTY_ROUTE : EMPTY_SCHEDULE
    setData(current => ({ ...current, [section]: [...(current[section] ?? []), { ...template }] }))
    setSelectedIndex(items.length)
  }

  function removeItem(index) {
    setData(current => ({ ...current, [section]: (current[section] ?? []).filter((_, i) => i !== index) }))
    if (selectedIndex === index) setSelectedIndex(null)
  }

  function moveItem(index, direction) {
    const swapIndex = index + direction
    if (swapIndex < 0 || swapIndex >= items.length) return
    setData(current => {
      const list = [...(current[section] ?? [])]
      ;[list[index], list[swapIndex]] = [list[swapIndex], list[index]]
      return { ...current, [section]: list }
    })
    if (selectedIndex === index) setSelectedIndex(swapIndex)
    else if (selectedIndex === swapIndex) setSelectedIndex(index)
  }

  function applyJson() {
    try {
      const parsed = JSON.parse(jsonText)
      setData({ stops: parsed.stops ?? [], routes: parsed.routes ?? [], schedules: parsed.schedules ?? [] })
      setMessage('JSON을 적용했습니다.')
    } catch (err) { setError(`JSON 파싱 실패: ${err.message}`) }
  }

  async function saveDraft() {
    setBusy(true); setMessage(''); setError('')
    try {
      await saveShuttleDraft(data, { uid: auth.user.uid })
      savedSnapshotRef.current = JSON.stringify(data)
      const { draft: fresh } = await fetchShuttleContent()
      setDraft(fresh)
      setMessage('초안을 저장했습니다.')
    } catch (err) { setError(err.message) } finally { setBusy(false) }
  }

  async function discardDraft() {
    if (!window.confirm('저장된 초안을 버리고 배포된 내용으로 되돌릴까요?')) return
    setBusy(true); setMessage(''); setError('')
    try { await discardShuttleDraft(auth.user.uid); await load(); setMessage('초안을 버렸습니다.') }
    catch (err) { setError(err.message) } finally { setBusy(false) }
  }

  async function publish() {
    if (!validation.ok) { setError('치명적 오류가 있어 배포할 수 없습니다.'); return }
    if (!window.confirm('현재 draft를 일반 사용자에게 배포할까요?')) return
    setBusy(true); setMessage(''); setError('')
    try {
      const version = await publishShuttleData(data, { uid: auth.user.uid })
      await load()
      setMessage(`배포 완료: ${version}`)
    } catch (err) { setError(err.validation ? '치명적 오류가 있어 배포할 수 없습니다.' : err.message) } finally { setBusy(false) }
  }

  async function rollback(versionDoc) {
    if (!window.confirm(`${versionDoc.version} 버전으로 published를 복구할까요?`)) return
    setBusy(true); setMessage(''); setError('')
    try { const version = await rollbackShuttleData(versionDoc, { uid: auth.user.uid }); await load(); setMessage(`${versionDoc.version} → ${version}로 롤백했습니다.`) }
    catch (err) { setError(err.message) } finally { setBusy(false) }
  }

  if (loading) return <div className="empty-state">셔틀 데이터를 불러오는 중…</div>
  return (
    <>
      <header className="page-header"><div><p className="eyebrow">서비스</p><h1>셔틀</h1><p>정류장·노선·시간표를 수정하고 검증한 뒤 배포합니다. 배포하면 즉시 반영됩니다.</p></div>
        {canEdit && <div className="header-actions"><button className="button secondary" type="button" onClick={addItem}><Plus size={16} />{SECTION_LABELS[section]} 추가</button></div>}
      </header>

      {error && <div className="error-state">{error}</div>}
      {message && <div className="temporary-warning">{message}</div>}

      <section className="panel" style={{ padding: 16, marginBottom: 16 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', fontSize: 12 }}>
            <span className={`status-badge ${dirty ? 'new' : 'published'}`}>{dirty ? '미저장 변경 있음' : '변경 없음'}</span>
            <span className="muted">published: {production?.version ?? '없음'}{draft ? ` · 초안 저장 ${formatDateTime(draft.updatedAt)}` : ' · 초안 없음'}</span>
            <span className="muted">production 대비: {diff.map(d => `${SECTION_LABELS[d.section]} ${d.before}→${d.after}`).join(' · ')}</span>
            {!validation.ok && <span className="status-badge ended"><AlertTriangle size={11} style={{ verticalAlign: -1 }} /> 오류 {validation.errors.length}</span>}
            {validation.warnings.length > 0 && <span className="status-badge new">경고 {validation.warnings.length}</span>}
          </div>
          {canEdit && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              {draft && <button className="button secondary" type="button" disabled={busy} onClick={discardDraft}><RotateCcw size={16} />초안 버리기</button>}
              <button className="button secondary" type="button" disabled={busy || !dirty} onClick={saveDraft}><Save size={16} />초안 저장</button>
              <button className="button primary" type="button" disabled={busy || !validation.ok} onClick={publish}><Send size={16} />검증 후 배포</button>
              {versions.length > 0 && (
                <select className="select" style={{ width: 220 }} aria-label="버전 롤백" disabled={busy} value="" onChange={e => { const v = versions.find(item => item.version === e.target.value); if (v) rollback(v) }}>
                  <option value="">이전 버전으로 롤백…</option>
                  {versions.map(v => <option key={v.version} value={v.version}>{v.version} · {formatDateTime(v.createdAt)}</option>)}
                </select>
              )}
            </div>
          )}
        </div>
        {(!validation.ok || validation.warnings.length > 0) && (
          <ul style={{ margin: '12px 0 0', paddingLeft: 18, fontSize: 12 }}>
            {validation.errors.map((msg, idx) => <li key={`e${idx}`} style={{ color: 'var(--danger)' }}>{msg}</li>)}
            {validation.warnings.map((msg, idx) => <li key={`w${idx}`} className="muted">{msg}</li>)}
          </ul>
        )}
      </section>

      <div className="toolbar">
        {SECTIONS.map(s => <button key={s} type="button" className={`check-chip ${section === s ? 'active' : ''}`} style={section === s ? { background: 'var(--accent-soft)', borderColor: 'var(--accent)' } : undefined} onClick={() => { setSection(s); setSelectedIndex(null) }}>{SECTION_LABELS[s]} ({(data[s] ?? []).length})</button>)}
        <div style={{ flex: 1 }} />
        <button type="button" className={`check-chip ${mode === 'table' ? 'active' : ''}`} onClick={() => setMode('table')}>표 편집</button>
        <button type="button" className={`check-chip ${mode === 'json' ? 'active' : ''}`} onClick={() => setMode('json')}>JSON 보기</button>
      </div>

      {mode === 'json' ? (
        <section className="panel" style={{ padding: 12 }}>
          <textarea className="textarea" style={{ fontFamily: 'monospace', fontSize: 11, minHeight: 420 }} value={jsonText} onChange={e => setJsonText(e.target.value)} disabled={!canEdit} />
          {canEdit && <button className="button primary" type="button" style={{ marginTop: 10 }} onClick={applyJson}>JSON 적용</button>}
        </section>
      ) : (
        <section className={`panel split-view ${selected ? 'has-selection' : ''}`}>
          <div className="split-list">
            <div className="split-list-items">
              {items.length === 0 ? <div className="empty-state">{SECTION_LABELS[section]} 항목이 없습니다.</div> : items.map((item, index) => (
                <button type="button" key={index} className={`split-item ${index === selectedIndex ? 'active' : ''}`} onClick={() => setSelectedIndex(index)}>
                  <div className="split-item-top">
                    <strong>{item.name || item.id || item.routeId || `#${index + 1}`}</strong>
                    <span className={`status-badge ${item.active === false ? 'ended' : 'published'}`}>{item.active === false ? '비활성' : '활성'}</span>
                  </div>
                  <p>{section === 'stops' ? `${item.id} · ${item.lat ?? ''}, ${item.lng ?? ''}` : section === 'routes' ? `${item.id} · 정류장 ${(item.stopIds ?? []).length}개` : `${item.routeId} · ${item.departureTime} · ${item.semesterType}`}</p>
                  {canEdit && (
                    <div style={{ display: 'flex', gap: 4, marginTop: 6 }} onClick={e => e.stopPropagation()}>
                      <button type="button" className="icon-button" style={{ width: 30, height: 30 }} disabled={index === 0} onClick={() => moveItem(index, -1)} aria-label="위로"><ArrowUp size={14} /></button>
                      <button type="button" className="icon-button" style={{ width: 30, height: 30 }} disabled={index === items.length - 1} onClick={() => moveItem(index, 1)} aria-label="아래로"><ArrowDown size={14} /></button>
                      <button type="button" className="icon-button" style={{ width: 30, height: 30 }} onClick={() => removeItem(index)} aria-label="삭제"><Trash2 size={14} /></button>
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
          <article className="detail-panel">
            {!selected ? <div className="empty-state">왼쪽 목록에서 항목을 선택하거나 새로 만드세요.</div> : (
              section === 'stops' ? <StopEditor item={selected} set={setFieldsForIndex} canEdit={canEdit} /> :
              section === 'routes' ? <RouteEditor item={selected} set={setFieldsForIndex} canEdit={canEdit} allStops={data.stops ?? []} /> :
              <ScheduleEditor item={selected} set={setFieldsForIndex} canEdit={canEdit} allRoutes={data.routes ?? []} />
            )}
          </article>
        </section>
      )}
    </>
  )
}

function StopEditor({ item, set, canEdit }) {
  return (
    <div className="form-section"><h2>정류장 정보</h2><div className="field-grid">
      <div className="field"><label>ID</label><input className="input" disabled={!canEdit} value={item.id} onChange={e => set({ id: e.target.value })} /></div>
      <div className="field"><label>이름</label><input className="input" disabled={!canEdit} value={item.name} onChange={e => set({ name: e.target.value })} /></div>
      <div className="field"><label>위도(lat)</label><input className="input" disabled={!canEdit} value={item.lat ?? ''} onChange={e => set({ lat: e.target.value })} /></div>
      <div className="field"><label>경도(lng)</label><input className="input" disabled={!canEdit} value={item.lng ?? ''} onChange={e => set({ lng: e.target.value })} /></div>
      <div className="field"><label>유형</label><input className="input" disabled={!canEdit} value={item.type ?? ''} onChange={e => set({ type: e.target.value, category: e.target.value })} /></div>
      <div className="field full"><label>메모</label><input className="input" disabled={!canEdit} value={item.memo ?? ''} onChange={e => set({ memo: e.target.value })} /></div>
      <div className="field"><label className="check-chip"><input type="checkbox" disabled={!canEdit} checked={item.active !== false} onChange={e => set({ active: e.target.checked })} />활성</label></div>
    </div></div>
  )
}

function RouteEditor({ item, set, canEdit, allStops }) {
  const stopIds = item.stopIds ?? []
  function moveStop(index, direction) {
    const swap = index + direction
    if (swap < 0 || swap >= stopIds.length) return
    const next = [...stopIds]
    ;[next[index], next[swap]] = [next[swap], next[index]]
    set({ stopIds: next })
  }
  return (
    <div className="form-section"><h2>노선 정보</h2><div className="field-grid">
      <div className="field"><label>ID</label><input className="input" disabled={!canEdit} value={item.id} onChange={e => set({ id: e.target.value })} /></div>
      <div className="field"><label>이름</label><input className="input" disabled={!canEdit} value={item.name} onChange={e => set({ name: e.target.value })} /></div>
      <div className="field"><label>방향</label>
        <select className="select" disabled={!canEdit} value={item.direction ?? 'toCampus'} onChange={e => set({ direction: e.target.value })}>
          <option value="toCampus">등교(toCampus)</option><option value="fromCampus">하교(fromCampus)</option><option value="loop">순환(loop)</option>
        </select>
      </div>
      <div className="field"><label>요금</label><input className="input" disabled={!canEdit} value={item.fare ?? ''} onChange={e => set({ fare: e.target.value })} /></div>
      <div className="field full"><label>메모</label><input className="input" disabled={!canEdit} value={item.memo ?? ''} onChange={e => set({ memo: e.target.value })} /></div>
      <div className="field"><label className="check-chip"><input type="checkbox" disabled={!canEdit} checked={item.active !== false} onChange={e => set({ active: e.target.checked })} />활성</label></div>
    </div>
      <p className="label" style={{ marginTop: 16, marginBottom: 8 }}>정류장 순서</p>
      {stopIds.length === 0 && <p className="field-help">정류장이 아직 없습니다.</p>}
      {stopIds.map((stopId, index) => (
        <div key={index} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0' }}>
          <span style={{ flex: 1, fontSize: 12 }}>{allStops.find(s => s.id === stopId)?.name ?? stopId} <span className="muted">({stopId})</span></span>
          <button type="button" className="icon-button" style={{ width: 30, height: 30 }} disabled={!canEdit || index === 0} onClick={() => moveStop(index, -1)}><ArrowUp size={14} /></button>
          <button type="button" className="icon-button" style={{ width: 30, height: 30 }} disabled={!canEdit || index === stopIds.length - 1} onClick={() => moveStop(index, 1)}><ArrowDown size={14} /></button>
          <button type="button" className="icon-button" style={{ width: 30, height: 30 }} disabled={!canEdit} onClick={() => set({ stopIds: stopIds.filter((_, i) => i !== index) })}><Trash2 size={14} /></button>
        </div>
      ))}
      {canEdit && (
        <select className="select" value="" onChange={e => { if (e.target.value) set({ stopIds: [...stopIds, e.target.value] }) }}>
          <option value="">+ 정류장 추가…</option>
          {allStops.filter(s => !stopIds.includes(s.id)).map(s => <option key={s.id} value={s.id}>{s.name} ({s.id})</option>)}
        </select>
      )}
    </div>
  )
}

function ScheduleEditor({ item, set, canEdit, allRoutes }) {
  const days = item.days ?? []
  const exceptions = item.exceptionDates ?? []
  function toggleDay(day) {
    set({ days: days.includes(day) ? days.filter(d => d !== day) : [...days, day] })
  }
  function updateException(index, patch) {
    set({ exceptionDates: exceptions.map((e, i) => i === index ? { ...e, ...patch } : e) })
  }
  return (
    <div className="form-section"><h2>시간표 정보</h2><div className="field-grid">
      <div className="field"><label>노선</label>
        <select className="select" disabled={!canEdit} value={item.routeId ?? ''} onChange={e => set({ routeId: e.target.value })}>
          <option value="">선택…</option>
          {allRoutes.map(r => <option key={r.id} value={r.id}>{r.name} ({r.id})</option>)}
        </select>
      </div>
      <div className="field"><label>출발 시각 (HH:mm)</label><input className="input" disabled={!canEdit} value={item.departureTime ?? ''} onChange={e => set({ departureTime: e.target.value })} placeholder="08:30" /></div>
      <div className="field"><label>학기 구분</label>
        <select className="select" disabled={!canEdit} value={item.semesterType ?? 'regular'} onChange={e => set({ semesterType: e.target.value })}>
          <option value="regular">정규학기(regular)</option><option value="vacation">방학(vacation)</option>
        </select>
      </div>
      <div className="field"><label className="check-chip"><input type="checkbox" disabled={!canEdit} checked={item.active !== false} onChange={e => set({ active: e.target.checked })} />활성</label></div>
      <div className="field full"><span className="label">요일</span><div className="checkbox-group">{DAY_OPTIONS.map(([value, label]) => <label className="check-chip" key={value}><input type="checkbox" disabled={!canEdit} checked={days.includes(value)} onChange={() => toggleDay(value)} />{label}</label>)}</div></div>
    </div>
      <p className="label" style={{ marginTop: 16, marginBottom: 4 }}>예외 날짜</p>
      <p className="field-help" style={{ marginBottom: 8 }}>일반 학기/방학 구분보다 우선합니다. active 해제 시 그 날짜만 운행 제외, active 유지 시 방학이어도 강제 운행.</p>
      {exceptions.map((exception, index) => (
        <div key={index} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0' }}>
          <input className="input" style={{ maxWidth: 160 }} type="date" disabled={!canEdit} value={exception.date ?? ''} onChange={e => updateException(index, { date: e.target.value })} />
          <label className="check-chip"><input type="checkbox" disabled={!canEdit} checked={exception.active !== false} onChange={e => updateException(index, { active: e.target.checked })} />운행(active)</label>
          <button type="button" className="icon-button" style={{ width: 30, height: 30 }} disabled={!canEdit} onClick={() => set({ exceptionDates: exceptions.filter((_, i) => i !== index) })}><Trash2 size={14} /></button>
        </div>
      ))}
      {canEdit && <button type="button" className="button secondary" style={{ marginTop: 8 }} onClick={() => set({ exceptionDates: [...exceptions, { date: '', active: false }] })}><Plus size={14} />예외 날짜 추가</button>}
    </div>
  )
}
